import type { ReactNode } from 'react'
import { Fragment } from 'react'
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import toast from 'react-hot-toast'
import { twJoin } from 'tailwind-merge'
import useUser from 'shared/hooks/use-user'
import PinIcon from 'shared/icons/pin-icon'
import { communityPostKeys } from 'modules/community/api/key-factory'
import { usePinPost } from 'modules/community/api/use-pin-post'
import { mutatePostsByPin } from 'modules/community/components/pinned-post/utils'
import useIsUserMember from 'modules/community/hooks/use-is-user-member'
import type { PostInterface } from 'modules/community/types/post-interface'
import type { PostPinnedToType } from 'modules/community/types/post-pinned-to-type'
import { hasPermissions } from 'modules/community/utils/abac-roles-permissions'
import { isPinnedIn } from 'modules/community/utils/pinned-post'

interface PostPinnedActionsProps {
  post: PostInterface
}

export const PostPinnedActions = ({ post }: PostPinnedActionsProps): ReactNode => {
  const { t } = useTranslation()
  const { query } = useRouter()
  const { communityPath, topicPath } = query
  const { user } = useUser()
  const { member } = useIsUserMember()
  const queryClient = useQueryClient()
  const pinPostMutation = usePinPost({ postId: post.id })

  const handlePinPost = async (post: PostInterface, pinTo: PostPinnedToType): Promise<void> => {
    try {
      await pinPostMutation.trigger({ source: pinTo })

      const shouldRemoveFromGlobal =
        (pinTo === 'community' && !topicPath) || (pinTo === 'topic' && !!topicPath)

      queryClient.setQueryData(
        communityPostKeys.pinnedList(communityPath, topicPath),
        mutatePostsByPin({
          post,
          shouldAppend: shouldRemoveFromGlobal,
          modifier: pinnedTo => [...pinnedTo, pinTo],
        }),
      )

      if (shouldRemoveFromGlobal) {
        const listKey = topicPath
          ? communityPostKeys.topicList(communityPath as string, topicPath as string)
          : communityPostKeys.list(communityPath as string)

        queryClient.setQueriesData<{ pages: { items: PostInterface[] }[] }>(
          { queryKey: listKey },
          old => {
            if (!old || !('pages' in old)) return old
            return {
              ...old,
              pages: old.pages.map(page => ({
                ...page,
                items: page.items.filter(p => p.id !== post.id),
              })),
            }
          },
        )
      }

      toast.success(t('community.actions.pin_post.success'))
    } catch {
      toast.error(t('community.actions.pin_post.error'))
    }
  }

  const [isPostPinnable] = hasPermissions(user, 'posts', ['pin'], { post, member })

  if (!isPostPinnable) return null

  return (
    <div className="flex items-center">
      <Menu as="div" className="relative flex size-7.5" onClick={e => e.stopPropagation()}>
        <MenuButton className="size-7" onClick={e => e.stopPropagation()}>
          <PinIcon className="size-4" />
        </MenuButton>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <MenuItems
            as="ul"
            anchor="bottom end"
            className="flex w-max max-w-sm min-w-36 flex-col rounded-[10px] bg-background-secondary px-5 py-3 shadow-menu"
          >
            {(['community', 'topic'] as PostPinnedToType[]).map(source => {
              const isPinned = isPinnedIn(post, source)
              return (
                <MenuItem key={`pin-target-${source}`} as="li">
                  <button
                    className="flex w-full items-center gap-1.5 rounded-md py-1.5 text-sm text-text-primary hover:text-text-primary/70 focus:text-text-primary/70"
                    onClick={() => handlePinPost(post, source)}
                    disabled={isPinned}
                  >
                    <PinIcon
                      aria-hidden
                      className={twJoin(
                        'size-4 text-text-secondary opacity-0',
                        isPinned && 'opacity-100',
                      )}
                    />
                    <span className="mr-auto">{t(`community.actions.pin_post.${source}`)}</span>
                  </button>
                </MenuItem>
              )
            })}
          </MenuItems>
        </Transition>
      </Menu>
    </div>
  )
}
